<div>
  <!-- Uploading notice -->
  <div v-if="is_uploading" class="text-center text-xxs my-3">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Uploading...</span>
    </div>
    <p class="mb-0">Uploading document...</p>
  </div>

  <!-- Hidden file input -->
  <input
    type="file"
    ref="warehouse_file"
    class="d-none"
    @change="handleFileUpload"
    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif,.bmp"
  />

  <div class="px-1">
    <!-- Breadcrumbs -->
    <nav class="mb-0 mt-3 p-0 px-2 text-xxs">
      <ol class="breadcrumb mb-2">
        <li class="breadcrumb-item text-dark fw-bold" :class="{ active: folderStack.length === 0 }">
          <span v-if="folderStack.length === 0">DocumentWarehouse</span>
          <a v-else href="#" @click.prevent="goBack" class="text-decoration-none text-dark">DocumentWarehouse</a>
        </li>
        <li
          v-for="(folder, index) in folderStack"
          :key="folder.id"
          class="breadcrumb-item fw-bold"
          :class="{ active: index === folderStack.length - 1 }"
        >
          <a
            v-if="index !== folderStack.length - 1"
            href="#"
            @click.prevent="goBack()"
            class="text-decoration-none text-dark"
          >
            {{ folder.folder_name }}
          </a>
          <span v-else>{{ folder.folder_name }}</span>
        </li>
      </ol>
    </nav>

    <!-- Main card -->
    <div class="card border rounded">

      <!-- Header: SHOW buttons ONLY when there ARE items -->
      <div class="card-header border-bottom d-flex align-items-center justify-content-between py-2">

        <div
          class="d-flex align-items-center"
          v-if="currentFolders.length > 0 || currentFiles.length > 0"
        >
          <button
          v-if="can_create"
            class="btn btn-sm btn-primary text-xxs me-2 rounded"
            @click="open_create_folder_modal"
          >
            <i class="bi bi-plus-lg"></i> New Folder
          </button>

          <button
            class="btn btn-sm btn-primary text-xxs"
            @click="triggerFileUpload"
            v-if="folderStack.length > 0 && can_upload"
          >
            <i class="bi bi-upload"></i> Upload Document
          </button>
        </div>
      </div>

      <div class="card-body">
        <!-- 1. Loading state -->
        <div v-if="isLoading" class="text-center py-5 w-100">
          <div class="spinner-border text-secondary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2 mb-0 text-muted text-xxs">Loading Documents...</p>
        </div>

        <!-- 3. Normal content (only when NOT loading and NO error) -->
        <div v-else>
          <!-- Bulk move -->
          <div v-if="selectedDocumentIds.length > 0" class="mb-2 table-responsive">
            <button class="btn btn-primary btn-sm" @click="openMoveToFolderModal">
              Move Selected ({{ selectedDocumentIds.length }}) to Folder
            </button>
          </div>

          <!-- Items table -->
          <!-- Items table -->
<table
  v-if="currentFolders.length > 0 || currentFiles.length > 0"
  class="table align-middle table-striped table-borderless mb-0"
>
  <thead class="text-uppercase btn-primary text-white sticky-top">
    <tr class="text-xxs">

      <!-- SELECT ALL -->
      <th class="text-xxs fw-light">
        <input
          v-if="folderStack.length > 0"
          type="checkbox"
          @change="toggleSelectAll($event)"
          :checked="selectedDocumentIds.length === currentFiles.length && currentFiles.length > 0"
        />
      </th>

      <!-- ICON -->
      <th class="text-xxs fw-light text-center">
        <i class="bi bi-file-earmark"></i>
      </th>

      <!-- NAME -->
      <th class="text-xxs fw-light">Name</th>

      <!-- CREATED -->
      <th class="text-xxs fw-light">Created</th>

      <!-- APPROVED BY (HIDDEN) -->
      <th class="text-xxs fw-light d-none">Approved by</th>

      <!-- ACTIONS -->
      <th class="text-xxs fw-light">Actions</th>

      <th></th>
    </tr>
  </thead>

  <tbody>

    <!-- FOLDERS -->
    <FolderView
      v-for="folder in currentFolders"
      :key="folder.id"
      :folder="folder"
      :can_delete="can_delete"
      :can_edit="can_edit"
      :portal_admin="user_details.user_role === 'admin' || portal_admin"
      :current_portal_permissions="current_portal_permissions"
      @open-folder="handleOpenFolder"
      @folder-deleted="remove_folder(folder.id)"
      @edit-folder="open_edit_folder_modal"
    />

    <!-- FILES -->
    <tr v-for="fileItem in currentFiles" :key="'file-' + fileItem.id">

      <!-- SELECT (column must match header) -->
      <td class="text-center">
        <input
          type="checkbox"
          :value="fileItem.id"
          v-model="selectedDocumentIds"
        />
      </td>

      <!-- ICON -->
      <td class="text-center">
        <img
          :src="getFileIcon(fileItem.file.s3_url)"
          class="file-icon download-icon w-20px"
        />
      </td>

      <!-- NAME -->
      <td class="text-xxs text-dark">
        <div @click="previewDocument(fileItem)">
          <span class="file-name">
            {{ getFileName(fileItem.file.s3_url) }}
          </span>
          <a
            :href="fileItem.file.s3_url"
            download
            :title="getFileName(fileItem.file.s3_url)"
            class="download-btn ms-2"
            @click.stop
            v-if="can_download"
          >
            <img
              src="https://cdn-icons-png.flaticon.com/128/724/724933.png"
              class="download-icon w-20px"
            />
          </a>
        </div>
      </td>

      <!-- CREATED -->
      <td class="text-xxs text-dark">
        {{ fileItem.file.date || "—" }}
      </td>

      <!-- APPROVED BY (HIDDEN & ALIGNED) -->
      <td class="text-xxs text-dark d-none">
        <ul class="list-unstyled mb-1 d-none">
          <li v-for="user in fileItem.approved_by" :key="user.id">
            {{ user?.first_name }} {{ user?.last_name }}
          </li>
        </ul>
      </td>

      <!-- ACTIONS -->
      <td>
        <button
          v-if="can_delete"
          class="btn btn-danger btn-sm text-xxs rounded"
          @click.stop="delete_folder_document(fileItem.id)"
        >
          Delete
        </button>
      </td>

      <td></td>
    </tr>
  </tbody>
</table>


          <!-- Empty state (only after load + no error + no items) -->
          <div
            v-else
            class="d-flex flex-column align-items-center justify-content-center text-center py-5 my-4 gap-2"
          >
            <i class="bi bi-inbox fs-1 d-block text-secondary" aria-hidden="true"></i>
            <p class="mb-2 fw-semibold text-secondary">No items yet</p>

            <!-- Big CTA on empty: root = only New Folder; inside a folder = New Folder + Upload -->
            <div class="d-flex gap-2">
              <button
              v-if="can_create"
                class="btn btn-primary btn-sm px-4 text-xxs rounded"
                @click="open_create_folder_modal"
              ><i class="bi bi-plus-lg"></i>New Folder
              </button>
              <button
                v-if="folderStack.length > 0 && can_upload"
                class="btn btn-sm btn-secondary text-xxs"
                @click="triggerFileUpload"
              >
                <i class="bi bi-upload"></i> Upload Document
              </button>

            </div>
          </div>
          <!-- /Empty state -->
        </div>
      </div>

    </div>
    <!-- /card -->
  </div>
</div>

 <div id="move_to_folder_modal" class="modal fade" tabindex="-1" aria-labelledby="moveToFolderModalLabel" aria-hidden="false">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content border-0 shadow-lg">
      <div class="modal-header">
        <h5 class="modal-title text-xxs fw-500">Move {{ selectedDocumentIds.length }} Document(s) to Folder</h5>
<button type="button" class="btn-close text-xxs fw-500" data-bs-dismiss="modal" aria-label="Close"></button>

      </div>
      <div class="modal-body modal-body-scroll">
        <select v-model="selectedFolderId" class="form-select">
          <option disabled value="">-- Select Folder --</option>
          <option v-for="folder in currentFolders" :value="folder.id" :key="folder.id">
            {{ folder.folder_name }}
          </option>
        </select>
      </div>
      <div class="modal-footer">
        <button class="btn btn-primary btn-sm" @click="moveSelectedDocumentsToFolder" :disabled="!selectedFolderId">
          Move
        </button>
      </div>
    </div>
  </div>
</div>

<div class="modal fade" id="staticBackdrop" tabindex="-1" aria-labelledby="staticBackdropLabel" aria-hidden="true">
  <div class="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header bg-primary text-white position-relative">
        <p class="modal-title text-xxs fw-500" id="exampleModalLabel">
          {{ is_editing_folder ? 'Edit Folder' : 'Create Folder' }}
        </p>
        <!-- Close button on the end with white "X" -->
          <button
            type="button"
            class="btn-close btn-close-white position-absolute end-0 top-50 translate-middle-y me-2"
            data-bs-dismiss="modal"
            aria-label="Close">
          </button>
      </div>
      <div class="modal-body">
        <input type="text" v-model="folder_name" placeholder="Enter folder name" class="form-control form-control-sm text-xxs shadow-sm" />
        <span v-if="validation_error" class="text-danger text-xxs mt-1">{{ validation_error }}</span>
      </div>
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-primary text-xxs rounded"
          @click="is_editing_folder ? update_folder() : create_folder()"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</div>

<div class="modal fade" id="document_preview_modal" tabindex="-1" aria-labelledby="documentPreviewModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="documentPreviewModalLabel">Document Preview</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <!-- Dynamically display document content -->
        <div v-if="previewDocumentType === 'pdf'">
          <embed :src="previewDocumentUrl" type="application/pdf" width="100%" height="600px">
        </div>
        <div v-else-if="previewDocumentType === 'word'">
          <!-- Word document viewer -->
          <iframe :src="previewDocumentUrl" width="100%" height="600px"></iframe>
        </div>
        <div v-else-if="previewDocumentType === 'excel'">
          <!-- Excel document viewer -->
          <iframe :src="previewDocumentUrl" width="100%" height="600px"></iframe>
        </div>

        <div v-else-if="previewDocumentType === 'image'">
          <img :src="previewDocumentUrl" class="img-fluid" alt="Document Image">
        </div>

        <div v-else>
          <p class="text-center">No preview available for this document type.</p>
        </div>

      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
      </div>
    </div>
  </div>
</div>
